import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, ExternalLink, Code, Cpu, Database, Globe, Award, ChevronDown, Terminal, Briefcase, Star, GitFork, Eye, Instagram, Languages } from 'lucide-react';

const Portfolio = () => {
  const [scrolled, setScrolled] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [githubProjects, setGithubProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const fullText = "Embedded Software Engineer";
  const mail = "mailto:marco@marcs7.it"

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setTypewriterText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Fetch GitHub repositories
  useEffect(() => {
    const fetchGitHubRepos = async () => {
      try {
        const response = await fetch('https://api.github.com/users/marcs7/repos?sort=updated&per_page=6');
        const data = await response.json();
        
        const formattedRepos = data.map(repo => ({
          id: repo.id,
          title: repo.name,
          description: repo.description || 'No description available',
          language: repo.language,
          stars: repo.stargazers_count,
          forks: repo.forks_count,
          watchers: repo.watchers_count,
          url: repo.html_url,
          topics: repo.topics || [],
          updated: new Date(repo.updated_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short'
          })
        }));
        
        setGithubProjects(formattedRepos);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching GitHub repos:', error);
        setLoading(false);
        // Fallback projects if API fails
        setGithubProjects([
          {
            id: 1,
            title: "WeatherCheck",
            description: "Simply python code to check the weather using openweathermap APIs",
            language: "Python",
            stars: 0,
            forks: 0,
            url: "https://github.com/marcs7/WeatherCheck"
          },
          {
            id: 2,
            title: "RCV-00",
            description: "Simple car controlled by Xbox360 controller",
            language: "C++",
            stars: 0,
            forks: 0,
            url: "https://github.com/marcs7/RCV-00"
          },
          {
            id: 3,
            title: "WeatherControl",
            description: "Simple weather control using a NodeMCU ESP8266",
            language: "C++",
            stars: 0,
            forks: 0,
            url: "https://github.com/marcs7/WeatherControl"
          },
          {
            id: 4,
            title: "ScrapMyUniverstyLibrary",
            description: "Python script that allows me to scrap the book list of my university and send the response on Telegram",
            language: "Python",
            stars: 0,
            forks: 0,
            url: "https://github.com/marcs7/ScrapMyUniverstyLibrary"
          }
        ]);
      }
    };

    fetchGitHubRepos();
  }, []);

  const skills = {
    embedded: {
      title: "Embedded Systems",
      icon: <Cpu className="w-5 h-5" />,
      items: ["C/C++", "STM32", "ESP32", "Arduino", "Raspberry Pi", "PlatformIO"]
    },
    web: {
      title: "Web Development",
      icon: <Globe className="w-5 h-5" />,
      items: ["React.js", "JavaScript", "HTML5", "CSS3", "PHP", "Node.js"]
    },
    database: {
      title: "Database & Tools",
      icon: <Database className="w-5 h-5" />,
      items: ["SQL", "Git", "VS Code", "Linux", "Docker", "CI/CD"]
    },
    languages: {
      title: "Programming",
      icon: <Code className="w-5 h-5" />,
      items: ["Python", "C", "C++", "JavaScript", "TypeScript", "Bash"]
    },
    spoken: {
      title: "Languages",
      icon: <Languages className="w-5 h-5" />,
      items: ["Italian (Native)", "English"]
    }
  };

  const experience = {
    company: "Abinsula",
    position: "Embedded Software Engineer",
    period: "February 2019 - Present",
    duration: "6+ years",
    description: "Abinsula is one of the leading Italian players in Embedded, IoT, Web and Mobile solutions. The core business is represented by the automotive sector, where it has established itself as a provider of innovative ICT solutions worldwide.",
    highlights: [
      "Firmware development for STM32 and ESP32 microcontrollers",
      "Implementation of communication protocols (I2C, SPI, UART, CAN)",
      "IoT systems design with cloud integration",
      "Code optimization for real-time systems",
      "Web interface development for embedded device configuration",
      "Creation of custom software solutions for integrated systems",
      "Participation in automotive and Industry 4.0 projects"
    ]
  };

  const languageColors = {
    'Python': 'bg-yellow-100 text-yellow-800',
    'C++': 'bg-blue-100 text-blue-800',
    'C': 'bg-gray-100 text-gray-800',
    'JavaScript': 'bg-amber-100 text-amber-800',
    'TypeScript': 'bg-blue-100 text-blue-800',
    'HTML': 'bg-orange-100 text-orange-800',
    'CSS': 'bg-purple-100 text-purple-800',
    'PHP': 'bg-indigo-100 text-indigo-800',
    'Shell': 'bg-green-100 text-green-800'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Terminal className="w-6 h-6 text-blue-600" />
              <span className="font-bold text-xl text-gray-900">Marco Satta</span>
            </div>
            <div className="hidden md:flex space-x-8">
              {['Home', 'About', 'Skills', 'Projects', 'Experience', 'Contact'].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
                >
                  {item}
                </a>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-6">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
              Available for projects
            </div>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Hi, I'm <span className="text-blue-600">Marco</span>
          </h1>
          <div className="h-12 mb-8">
            <p className="text-xl md:text-2xl text-gray-600">
              {typewriterText}<span className="animate-pulse">|</span>
            </p>
          </div>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Specialized in firmware development, IoT systems and full-stack solutions.
            I transform complex ideas into elegant and efficient embedded solutions.
          </p>
          <div className="flex justify-center space-x-4 mb-12">
            <a href="#contact" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
              Contact Me
            </a>
            <a href="#projects" className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-blue-600 hover:text-blue-600 transition-all duration-200 font-medium">
              View Projects
            </a>
          </div>
          <div className="flex justify-center space-x-6">
            <a href="https://github.com/marcs7" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors duration-200">
              <Github className="w-6 h-6" />
            </a>
            <a href="https://www.linkedin.com/in/marcosatta/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors duration-200">
              <Linkedin className="w-6 h-6" />
            </a>
            <a href={mail} className="text-gray-600 hover:text-blue-600 transition-colors duration-200">
              <Mail className="w-6 h-6" />
            </a>
          </div>
          <div className="mt-16 animate-bounce">
            <ChevronDown className="w-6 h-6 mx-auto text-gray-400" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">About Me</h2>
          <p className="text-center text-gray-600 mb-12">Passion for innovation and technical excellence</p>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl p-8 flex items-center justify-center overflow-hidden">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/EC_Mores24Agosto.jpg`}
                  alt="Marco Satta"
                  className="w-full h-full object-cover rounded-xl"
                  style={{ maxHeight: '400px', maxWidth: '400px' }}
                />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Embedded Software Engineer @ Abinsula</h3>
              <p className="text-gray-600 mb-6">
                With over 6 years of experience in the embedded sector, I design and develop
                software solutions for embedded systems and IoT. My passion for technology has
                led me to specialize in both firmware development and web development, allowing me
                to create complete end-to-end solutions.
              </p>
              <p className="text-gray-600 mb-6">
                At Abinsula, one of the leading Italian players in Embedded, IoT, Web and Mobile solutions,
                I contribute to the development of innovative solutions for the automotive sector and Industry 4.0.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">6+ years of professional experience</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Code className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Full-stack developer with embedded focus</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Award className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Sport Guide Certification</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">Technical Skills</h2>
          <p className="text-center text-gray-600 mb-12">Complete technology stack for innovative solutions</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {Object.entries(skills).map(([key, category]) => (
              <div key={key} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    {category.icon}
                  </div>
                  <h3 className="font-bold text-gray-900">{category.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {category.items.map((skill) => (
                    <span key={skill} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-blue-100 hover:text-blue-700 transition-colors duration-200">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GitHub Projects Section */}
      <section id="projects" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">GitHub Projects</h2>
          <p className="text-center text-gray-600 mb-12">My latest open source projects</p>
          
          {loading ? (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading projects from GitHub...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {githubProjects.map((project) => (
                <a 
                  key={project.id} 
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 block"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-200 flex-1">
                      {project.title}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-200 ml-2" />
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {project.description}
                  </p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                      <span className="flex items-center space-x-1">
                        <Star className="w-3 h-3" />
                        <span>{project.stars}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <GitFork className="w-3 h-3" />
                        <span>{project.forks}</span>
                      </span>
                      {project.watchers && (
                        <span className="flex items-center space-x-1">
                          <Eye className="w-3 h-3" />
                          <span>{project.watchers}</span>
                        </span>
                      )}
                    </div>
                    {project.updated && (
                      <span className="text-xs text-gray-500">{project.updated}</span>
                    )}
                  </div>
                  
                  {project.language && (
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        languageColors[project.language] || 'bg-gray-100 text-gray-700'
                      }`}>
                        {project.language}
                      </span>
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
          
          <div className="text-center mt-8">
            <a 
              href="https://github.com/marcs7?tab=repositories" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>View all repositories</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">Professional Experience</h2>
          <p className="text-center text-gray-600 mb-12">My professional journey</p>
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{experience.position}</h3>
                      <p className="text-lg text-blue-600 font-medium">{experience.company}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{experience.period}</p>
                      <p className="text-sm font-medium text-gray-700">{experience.duration}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{experience.description}</p>
                  <div className="space-y-2">
                    {experience.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                        <p className="text-gray-700 text-sm">{highlight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4 text-white">Contact me</h2>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a href={mail} className="flex items-center space-x-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200 font-medium">
              <Mail className="w-5 h-5" />
              <span>Email</span>
            </a>
            <a href="https://www.linkedin.com/in/marcosatta/" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors duration-200 font-medium">
              <Linkedin className="w-5 h-5" />
              <span>LinkedIn</span>
            </a>
            <a href="https://github.com/marcs7" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium">
              <Github className="w-5 h-5" />
              <span>GitHub</span>
            </a>
            <a href="https://instagram.com/lifeby.marco" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium">
              <Instagram className="w-5 h-5" />
              <span>Instagram</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">
            © 2025 Marco Satta - Built with React and ❤️
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;